import { AppBreadcrumbs } from "@/components/layouts/app-breadcrumbs";
import PageLayout from "@/components/layouts/page.layout";
import { SearchInput } from "@/components/elements/search-input";
import { AppEntityTable } from "@/components/blocks/app-entity-table";
import { PAGE_META } from "@/config/page-meta";
import { RoutePaths } from "@/config/route-paths";
import { useAuth } from "@/contexts/auth";
import { EmptyOrganizationsList } from "@/features/organizations/pages/list/components/empty-organizations-list";
import { ErrorOrganizationsList } from "@/features/organizations/pages/list/components/error-organizations-list";
import { FilteredEmptyOrganizationsList } from "@/features/organizations/pages/list/components/filtered-empty-organizations-list";
import { LoadingOrganizationsList } from "@/features/organizations/pages/list/components/loading-organizations-list";
import { useOrganizationListParams } from "@/features/organizations/pages/list/hooks/use-organization-list-params";
import { getOrganizationsListBreadcrumbs } from "@/features/organizations/pages/list/lib/get-organizations-list-breadcrumbs";
import { useOrganizationsListQuery } from "@/features/organizations/queries/use-organizations-list";
import { useAppListSearch } from "@/hooks/use-app-list-search";
import { usePageMeta } from "@/hooks/use-page-meta";
import { Button, ListSection, ListSectionFilters, PageHeader, SectionWithHeading } from "@cmde/shadcn";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { PageBackButton } from "@/components/elements/page-back-button";

export default function OrganizationsListPage() {
  const { t } = useTranslation();
  const { customerId } = useOrganizationListParams();
  const { customers } = useAuth();
  const organizationsListQuery = useOrganizationsListQuery();

  const { inputValue, debouncedQuery, setInputValue, filteredData } = useAppListSearch({
    data: organizationsListQuery.data ?? [],
    keys: ["name", "organizationId", "sport"],
    debounceDelay: 300,
  });

  const customerName = useMemo(() => {
    return customers?.find((customer) => customer.customerId === customerId)?.name;
  }, [customers, customerId]);

  const breadcrumbs = useMemo(
    () => getOrganizationsListBreadcrumbs({ customerId, customerName }),
    [customerId, customerName],
  );

  const showFilteredEmpty =
    !organizationsListQuery.isLoading &&
    !organizationsListQuery.isError &&
    (organizationsListQuery.data?.length ?? 0) > 0 &&
    debouncedQuery.length > 0 &&
    filteredData.length === 0;

  return (
    <>
      {usePageMeta(PAGE_META.organizations)}
      <PageLayout>
        <div className="h-full">
          <PageHeader
            title={t("organizations.list.messages.title")}
            subtitle={t("organizations.list.messages.subtitle")}
            backAction={<PageBackButton href={RoutePaths.customers.list()} />}
            breadcrumbs={<AppBreadcrumbs breadcrumbs={breadcrumbs} />}
          />

          <SectionWithHeading heading="">
            <ListSection
              header={
                <ListSectionFilters
                  searchComponent={
                    <SearchInput
                      value={inputValue}
                      onChange={setInputValue}
                      placeholder={t("organizations.list.search.placeholder")}
                      disabled={organizationsListQuery.isLoading}
                    />
                  }
                />
              }
            >
              {showFilteredEmpty ? (
                <FilteredEmptyOrganizationsList />
              ) : (
                <AppEntityTable
                  queryResult={organizationsListQuery}
                  data={filteredData}
                  columns={[
                    {
                      header: t("organizations.list.table.name"),
                      key: "name",
                      cell: (organization) => (
                        <Link
                          to={RoutePaths.credentials.list({
                            customerId,
                            organizationId: organization.organizationId,
                            sport: organization.sport,
                          })}
                          className="hover:underline"
                        >
                          {organization.name}
                        </Link>
                      ),
                    },
                    {
                      header: t("organizations.list.table.organization_id"),
                      key: "organizationId",
                      cell: (organization) => organization.organizationId,
                    },
                    {
                      header: t("organizations.list.table.sport"),
                      key: "sport",
                      cell: (organization) => organization.sport,
                    },
                    {
                      header: "",
                      key: "actions",
                      width: "100px",
                      cell: (organization) => (
                        <Button variant="link" size="sm" asChild>
                          <Link
                            to={RoutePaths.credentials.list({
                              customerId,
                              organizationId: organization.organizationId,
                              sport: organization.sport,
                            })}
                          >
                            {t("organizations.list.table.users")}
                          </Link>
                        </Button>
                      ),
                    },
                  ]}
                  getKey={(organization) => organization.organizationId}
                  loadingContent={<LoadingOrganizationsList />}
                  errorContent={
                    <ErrorOrganizationsList
                      retry={() => organizationsListQuery.refetch()}
                      errorMessage={organizationsListQuery.error?.message}
                    />
                  }
                  emptyContent={<EmptyOrganizationsList />}
                />
              )}
            </ListSection>
          </SectionWithHeading>
        </div>
      </PageLayout>
    </>
  );
}
